import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowDown, ArrowUp, ChevronDown, ChevronRight, FileText, Pin, Plus, Save,
  Search, Settings2, Trash2, Upload,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { HelpButton } from '../components/HelpButton';

const cardCls = 'rounded-lg border border-line bg-surface p-4';
const selectCls = 'h-10 w-full px-3 text-sm rounded-md border border-line bg-surface text-fg';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);
const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }) : '—');

/* ── Master SOP document (one current PDF, older versions kept) ─────────── */
function MasterSop({ canEdit, canDelete }) {
  const [doc, setDoc] = useState({ current: null, history: [] });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get('/gsoc/sop/document').then(({ data }) => setDoc(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    try {
      await api.delete(`/gsoc/sop/document/${d.id}`);
      toast.success(`${d.filename} removed`);
      load();
    } catch (e) { err(e, 'Could not remove the file'); }
  };

  const open = async (d) => {
    try {
      const res = await api.get(`/gsoc/sop/document/${d.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open the SOP'); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await api.post('/gsoc/sop/document', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('SOP uploaded — this is now the current version');
      load();
    } catch (e2) { err(e2, 'Could not upload the SOP'); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="sop-master-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base md:text-lg font-semibold text-fg flex items-center gap-2">
            <FileText className="w-5 h-5" aria-hidden="true" /> Master SOP Document
          </h2>
          <p className="text-sm text-fg-muted mt-0.5 max-w-2xl">
            The full written SOP as one PDF. Upload a new one any time — the old versions stay on file so
            you can always see what the SOP said before.
          </p>
        </div>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={upload}
                   data-testid="sop-upload-input" />
            <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()}
                    data-testid="sop-upload-btn">
              <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
              {busy ? 'Uploading...' : doc.current ? 'Upload New Version' : 'Upload SOP PDF'}
            </Button>
          </>
        )}
      </div>

      <div className="mt-3">
        {doc.current ? (
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-line bg-surface-sunken px-3 py-2"
               data-testid="sop-current-doc">
            <Badge className="bg-[hsl(var(--success))] text-white border-0">CURRENT</Badge>
            <button type="button" onClick={() => open(doc.current)}
                    className="text-fg font-medium hover:underline" data-testid="sop-current-open">
              {doc.current.filename}
            </button>
            <span className="text-xs text-fg-muted">
              {kb(doc.current.size)} · uploaded by {doc.current.uploaded_by_name} {fmtStamp(doc.current.uploaded_at)}
            </span>
            {canDelete && (
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => remove(doc.current)}
                      title="Remove this file" data-testid="sop-current-remove">
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-fg-muted" data-testid="sop-no-doc">
            No SOP PDF on file yet. {canEdit ? 'Use Upload SOP PDF once yours is ready.' : 'Your supervisor will post it here.'}
          </p>
        )}

        {doc.history.length > 0 && (
          <div className="mt-3" data-testid="sop-history">
            <p className="text-xs uppercase tracking-wide text-fg-muted mb-1">Earlier versions</p>
            <div className="space-y-1">
              {doc.history.map((h) => (
                <div key={h.id} className="text-sm flex flex-wrap items-center gap-2">
                  <button type="button" onClick={() => open(h)} className="text-fg hover:underline"
                          data-testid={`sop-history-open-${h.id}`}>{h.filename}</button>
                  <span className="text-xs text-fg-muted">replaced {fmtStamp(h.superseded_at)}</span>
                  {canDelete && (
                    <button type="button" onClick={() => remove(h)} aria-label={`Remove ${h.filename}`}
                            className="text-fg-muted hover:text-danger" data-testid={`sop-history-remove-${h.id}`}>
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Category manager (supervisors) ────────────────────────────────────── */
function CategoryManager({ cats, reload }) {
  const [local, setLocal] = useState(cats);
  const [name, setName] = useState('');
  useEffect(() => { setLocal(cats); }, [cats]);

  const add = async () => {
    if (!name.trim()) return toast.error('Give the category a name');
    try {
      await api.post('/gsoc/sop/categories', { name: name.trim(), is_active: true });
      setName(''); toast.success('Category added'); reload();
    } catch (e) { err(e, 'Could not add the category'); }
  };

  const save = async (c) => {
    try {
      await api.put(`/gsoc/sop/categories/${c.id}`, { name: c.name, is_active: c.is_active });
      toast.success('Category saved'); reload();
    } catch (e) { err(e, 'Could not save the category'); }
  };

  const remove = async (c) => {
    try { await api.delete(`/gsoc/sop/categories/${c.id}`); toast.success('Category deleted'); reload(); }
    catch (e) { err(e, 'Could not delete the category'); }
  };

  const move = async (from, to) => {
    const next = [...local];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    setLocal(next);
    try { await api.post('/gsoc/sop/categories/reorder', { ids: next.map((x) => x.id) }); reload(); }
    catch (e) { err(e, 'Could not reorder'); setLocal(cats); }
  };

  return (
    <div className={cardCls} data-testid="sop-category-manager">
      <h3 className="font-semibold text-fg">Categories</h3>
      <p className="text-sm text-fg-muted mt-0.5">
        The groups your notes sit in, in the order shown here. Switch one off to hide it from operators;
        a category can only be deleted once it is empty.
      </p>
      <div className="mt-3 space-y-2">
        {local.map((c, i) => (
          <div key={c.id} className="flex flex-wrap items-center gap-2" data-testid={`sop-cat-row-${i}`}>
            <Input value={c.name} className="h-9 flex-1 min-w-[200px]" data-testid={`sop-cat-name-${i}`}
                   onChange={(e) => setLocal((l) => l.map((x) => x.id === c.id ? { ...x, name: e.target.value } : x))} />
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="px-2" disabled={i === 0}
                      onClick={() => move(i, i - 1)} title="Move up" data-testid={`sop-cat-up-${i}`}>
                <ArrowUp className="w-4 h-4" aria-hidden="true" />
              </Button>
              <Button variant="ghost" size="sm" className="px-2" disabled={i === local.length - 1}
                      onClick={() => move(i, i + 1)} title="Move down" data-testid={`sop-cat-down-${i}`}>
                <ArrowDown className="w-4 h-4" aria-hidden="true" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={c.is_active} data-testid={`sop-cat-active-${i}`}
                      onCheckedChange={(v) => setLocal((l) => l.map((x) => x.id === c.id ? { ...x, is_active: v } : x))} />
              <span className="text-xs text-fg-muted">Visible</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => save(c)} data-testid={`sop-cat-save-${i}`}>
              <Save className="w-4 h-4 mr-1" aria-hidden="true" />Save
            </Button>
            <Button variant="ghost" size="sm" onClick={() => remove(c)} data-testid={`sop-cat-delete-${i}`}>
              <Trash2 className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-sm">New category</Label>
          <Input value={name} className="mt-1 h-9" placeholder="e.g. Weather Events"
                 data-testid="sop-cat-new-name" onChange={(e) => setName(e.target.value)} />
        </div>
        <Button onClick={add} data-testid="sop-cat-add-btn">
          <Plus className="w-4 h-4 mr-1" aria-hidden="true" />Add Category
        </Button>
      </div>
    </div>
  );
}

/* ── One note row ──────────────────────────────────────────────────────── */
function NoteRow({ note, index, canEdit, canDelete, cats, reload }) {
  const [row, setRow] = useState(note);
  const [editing, setEditing] = useState(false);
  useEffect(() => { setRow(note); }, [note]);

  const save = async () => {
    try {
      await api.put(`/gsoc/sop/notes/${row.id}`, {
        category_id: row.category_id, title: row.title, body: row.body,
        is_pinned: !!row.is_pinned, is_active: !!row.is_active,
      });
      toast.success('Note saved'); setEditing(false); reload();
    } catch (e) { err(e, 'Could not save the note'); }
  };

  const remove = async () => {
    try { await api.delete(`/gsoc/sop/notes/${row.id}`); toast.success('Note deleted'); reload(); }
    catch (e) { err(e, 'Could not delete the note'); }
  };

  return (
    <div className={`rounded-md border bg-surface p-3 ${row.is_pinned ? 'border-[hsl(var(--warning-border))] border-l-4' : 'border-line'}`}
         data-testid={`sop-note-${index}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {row.is_pinned && (
              <Badge className="bg-warning-bg text-fg border border-warning-border" data-testid={`sop-note-pinned-${index}`}>
                <Pin className="w-3 h-3 mr-1" aria-hidden="true" />PINNED
              </Badge>
            )}
            <p className="font-semibold text-fg" data-testid={`sop-note-title-${index}`}>{row.title}</p>
            {!row.is_active && <Badge className="bg-surface-sunken text-fg-muted border border-line">off</Badge>}
          </div>
          <p className="text-xs text-fg-muted mt-0.5">
            updated by {row.updated_by_name} {fmtStamp(row.updated_at)}
          </p>
        </div>
        {(canEdit || canDelete) && (
          <div className="flex items-center gap-1">
            {canEdit && (
              <Button variant="ghost" size="sm" onClick={() => setEditing((v) => !v)}
                      data-testid={`sop-note-edit-${index}`}>{editing ? 'Close' : 'Edit'}</Button>
            )}
            {canDelete && (
              <Button variant="ghost" size="sm" onClick={remove} data-testid={`sop-note-delete-${index}`}>
                <Trash2 className="w-4 h-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        )}
      </div>

      {!editing && (
        <p className="text-sm text-fg mt-2 whitespace-pre-wrap" data-testid={`sop-note-body-${index}`}>{row.body}</p>
      )}

      {editing && (
        <div className="mt-3 space-y-2">
          <Input value={row.title} className="h-9" data-testid={`sop-note-title-input-${index}`}
                 onChange={(e) => setRow((r) => ({ ...r, title: e.target.value }))} />
          <Textarea rows={4} value={row.body} data-testid={`sop-note-body-input-${index}`}
                    onChange={(e) => setRow((r) => ({ ...r, body: e.target.value }))} />
          <div className="flex flex-wrap items-center gap-3">
            <select className={`${selectCls} max-w-xs`} value={row.category_id} data-testid={`sop-note-category-${index}`}
                    onChange={(e) => setRow((r) => ({ ...r, category_id: e.target.value }))}>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={!!row.is_pinned} data-testid={`sop-note-pin-${index}`}
                      onCheckedChange={(v) => setRow((r) => ({ ...r, is_pinned: v }))} />
              <span className="text-fg-muted">Pin to top</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={!!row.is_active} data-testid={`sop-note-active-${index}`}
                      onCheckedChange={(v) => setRow((r) => ({ ...r, is_active: v }))} />
              <span className="text-fg-muted">Visible to operators</span>
            </label>
            <Button size="sm" onClick={save} data-testid={`sop-note-save-${index}`}>
              <Save className="w-4 h-4 mr-1" aria-hidden="true" />Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function GSOCSOP() {
  const perms = useEffectivePermissions();
  const isAdmin = perms.role === 'admin';
  const canEdit = isAdmin || perms.can('gsoc_passon', 'edit');
  const canDelete = isAdmin || perms.can('gsoc_passon', 'delete');

  const [cats, setCats] = useState([]);
  const [notes, setNotes] = useState([]);
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [showCats, setShowCats] = useState(false);
  const [draft, setDraft] = useState({ category_id: '', title: '', body: '', is_pinned: false });

  const load = () => {
    api.get('/gsoc/sop/categories').then(({ data }) => setCats(data)).catch(() => {});
    api.get('/gsoc/sop/notes', { params: { include_inactive: canEdit } })
      .then(({ data }) => setNotes(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [canEdit]);

  const visibleCats = useMemo(() => cats.filter((c) => c.is_active || canEdit), [cats, canEdit]);

  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => n.title.toLowerCase().includes(q) || n.body.toLowerCase().includes(q));
  }, [notes, query]);

  const addNote = async () => {
    if (!draft.category_id) return toast.error('Pick a category');
    if (!draft.title.trim() || !draft.body.trim()) return toast.error('Give the note a title and a body');
    try {
      await api.post('/gsoc/sop/notes', {
        category_id: draft.category_id, title: draft.title.trim(), body: draft.body.trim(),
        is_pinned: !!draft.is_pinned, is_active: true,
      });
      setDraft({ category_id: draft.category_id, title: '', body: '', is_pinned: false });
      toast.success('SOP note added'); load();
    } catch (e) { err(e, 'Could not add the note'); }
  };

  return (
    <div className="space-y-5" data-testid="gsoc-sop-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="page-title">SOP</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-3xl">
            Your standing operating procedures — the master SOP PDF plus short reminder notes grouped by
            topic. This is a reference library, so nothing here has to be acknowledged. Anything you
            <strong> must </strong>read before logging activity still lives on the Pass-On Log.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton module="gsoc" label="Playbook" />
          {canEdit && (
            <Button variant="outline" onClick={() => setShowCats((v) => !v)} data-testid="sop-manage-cats-btn">
              <Settings2 className="w-4 h-4 mr-2" aria-hidden="true" />
              {showCats ? 'Hide Categories' : 'Manage Categories'}
            </Button>
          )}
        </div>
      </div>

      <MasterSop canEdit={canEdit} canDelete={canDelete} />

      {canEdit && showCats && <CategoryManager cats={cats} reload={load} />}

      {canEdit && (
        <div className={cardCls} data-testid="sop-new-note">
          <h3 className="font-semibold text-fg">Add an SOP note</h3>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Category</Label>
              <select className={`${selectCls} mt-1`} value={draft.category_id} data-testid="sop-new-category"
                      onChange={(e) => setDraft((d) => ({ ...d, category_id: e.target.value }))}>
                <option value="">Pick a category...</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">Title</Label>
              <Input value={draft.title} className="mt-1" placeholder="e.g. Always call the site before dispatch"
                     data-testid="sop-new-title" onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-sm">Note</Label>
            <Textarea rows={3} value={draft.body} className="mt-1" data-testid="sop-new-body"
                      placeholder="Write it the way you would say it out loud. Short steps work best."
                      onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={draft.is_pinned} data-testid="sop-new-pin"
                      onCheckedChange={(v) => setDraft((d) => ({ ...d, is_pinned: v }))} />
              <span className="text-fg-muted">Pin to the top of its category</span>
            </label>
            <Button onClick={addNote} data-testid="sop-new-save-btn">
              <Plus className="w-4 h-4 mr-1" aria-hidden="true" />Add Note
            </Button>
          </div>
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
        <Input value={query} className="pl-9" placeholder="Search every SOP note..."
               data-testid="sop-search" onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="space-y-3" data-testid="sop-category-list">
        {visibleCats.length === 0 && (
          <p className="text-sm text-fg-muted" data-testid="sop-empty">No SOP categories yet.</p>
        )}
        {visibleCats.map((c, ci) => {
          const rows = matched.filter((n) => n.category_id === c.id);
          const isOpen = query.trim() ? true : !collapsed[c.id];
          if (query.trim() && rows.length === 0) return null;
          return (
            <div key={c.id} className={cardCls} data-testid={`sop-category-${ci}`}>
              <button type="button" className="w-full flex items-center gap-2 text-left"
                      onClick={() => setCollapsed((s) => ({ ...s, [c.id]: !s[c.id] }))}
                      data-testid={`sop-category-toggle-${ci}`}>
                {isOpen ? <ChevronDown className="w-4 h-4" aria-hidden="true" />
                        : <ChevronRight className="w-4 h-4" aria-hidden="true" />}
                <span className="font-semibold text-fg">{c.name}</span>
                <span className="text-xs text-fg-muted">
                  {rows.length} {rows.length === 1 ? 'note' : 'notes'}
                </span>
                {!c.is_active && <Badge className="bg-surface-sunken text-fg-muted border border-line">hidden</Badge>}
              </button>
              {isOpen && (
                <div className="mt-3 space-y-2">
                  {rows.length === 0
                    ? <p className="text-sm text-fg-muted" data-testid={`sop-category-empty-${ci}`}>Nothing in here yet.</p>
                    : rows.map((n, i) => (
                        <NoteRow key={n.id} note={n} index={`${ci}-${i}`} cats={cats}
                                 canEdit={canEdit} canDelete={canDelete} reload={load} />
                      ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
